import React, { useState, useEffect } from 'react';
import { PenTool, ArrowRight, ArrowLeft, FileText, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../utils/api';
import PDFAnnotator from './PDFAnnotator';

const SignatureRequestDialog = ({ 
  open, 
  onOpenChange, 
  clientId, 
  files, 
  onSuccess 
}) => {
  const [step, setStep] = useState(1); // 1: Select & Details, 2: Annotate
  const [selectedFile, setSelectedFile] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [signatureFields, setSignatureFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    document_type: 'engagement_letter',
    title: '',
    description: '',
    expires_days: 30
  });

  const pdfFiles = files?.filter(f => f.mime_type === 'application/pdf') || [];

  useEffect(() => {
    if (!open) {
      // Reset state when dialog closes
      setStep(1);
      setSelectedFile(null);
      setPdfUrl(null);
      setSignatureFields([]);
      setForm({
        document_type: 'engagement_letter',
        title: '',
        description: '',
        expires_days: 30
      });
    }
  }, [open]);

  useEffect(() => {
    // Load PDF when file is selected and moving to step 2
    if (selectedFile && step === 2) {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      setPdfUrl(`${backendUrl}/api/files/${selectedFile.id}/preview?token=${token}`);
    }
  }, [selectedFile, step]);

  const handleFileSelect = (fileId) => {
    const file = pdfFiles.find(f => f.id === fileId);
    setSelectedFile(file);
    if (file) {
      setForm(prev => ({
        ...prev,
        title: file.filename.replace(/\.[^/.]+$/, '')
      }));
    }
  };

  const handleNext = () => {
    if (!selectedFile) {
      toast.error('Please select a document');
      return;
    }
    if (!form.title) {
      toast.error('Please enter a title');
      return;
    }
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleSubmit = async () => {
    if (signatureFields.length === 0) {
      // Allow submission without fields (legacy behavior)
      const confirmed = window.confirm(
        'No signature fields have been placed on the document. The client will be able to sign anywhere. Continue?'
      );
      if (!confirmed) return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file_id', selectedFile.id);
      formData.append('client_id', clientId);
      formData.append('document_type', form.document_type);
      formData.append('title', form.title);
      formData.append('description', form.description || '');
      formData.append('expires_days', form.expires_days);
      
      if (signatureFields.length > 0) {
        formData.append('signature_fields', JSON.stringify(signatureFields));
      }
      
      await api.post('/signature-requests', formData);
      toast.success('Signature request sent! Client will receive an email notification.');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Failed to create signature request:', error);
      toast.error(error.response?.data?.detail || 'Failed to create signature request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={step === 2 ? "max-w-6xl h-[90vh] flex flex-col" : "max-w-lg max-h-[90vh] overflow-y-auto"}>
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <PenTool className="w-5 h-5 text-teal-600" />
            Request Signature
            {step === 2 && (
              <span className="text-sm font-normal text-gray-500 ml-2">
                - Place signature fields
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <>
            <div className="space-y-4 overflow-y-auto flex-1">
              <div>
                <Label>Select Document</Label>
                <Select
                  value={selectedFile?.id || ''}
                  onValueChange={handleFileSelect}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a PDF document..." />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={5}>
                    {pdfFiles.map(file => (
                      <SelectItem key={file.id} value={file.id}>
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          {file.filename}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {pdfFiles.length === 0 && (
                  <p className="text-sm text-amber-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    No PDF documents available. Upload a PDF first.
                  </p>
                )}
              </div>
              
              <div>
                <Label>Document Type</Label>
                <Select
                  value={form.document_type}
                  onValueChange={(value) => setForm(prev => ({ ...prev, document_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={5}>
                    <SelectItem value="engagement_letter">Engagement Letter</SelectItem>
                    <SelectItem value="contract">Contract</SelectItem>
                    <SelectItem value="authorization">Authorization Form</SelectItem>
                    <SelectItem value="consent">Consent Form</SelectItem>
                    <SelectItem value="acknowledgment">Acknowledgment</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Title</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., 2024 Tax Engagement Letter"
                />
              </div>
              
              <div>
                <Label>Description (optional)</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Add any instructions or notes for the client..."
                  rows={2}
                />
              </div>
              
              <div>
                <Label>Expires in</Label>
                <Select
                  value={form.expires_days.toString()}
                  onValueChange={(value) => setForm(prev => ({ ...prev, expires_days: parseInt(value) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={5}>
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="14">14 days</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="60">60 days</SelectItem>
                    <SelectItem value="90">90 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter className="flex-shrink-0 pt-4 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleNext}
                disabled={!selectedFile}
                className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
              >
                Next: Place Fields
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 2 && (
          <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
            <div className="flex-1 min-h-0 border rounded-lg overflow-hidden">
              {pdfUrl && (
                <PDFAnnotator
                  pdfUrl={pdfUrl}
                  fields={signatureFields}
                  onFieldsChange={setSignatureFields}
                />
              )}
            </div>

            <div className="flex items-center justify-between pt-4 border-t mt-4 flex-shrink-0">
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-500">
                  {signatureFields.length} field{signatureFields.length !== 1 ? 's' : ''} placed
                </span>
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
                >
                  {loading ? 'Sending...' : 'Send Signature Request'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SignatureRequestDialog;
